import { useState } from "react";
import { toast } from "sonner";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { trackCallClick } from "@/lib/ga4";
import { HubSection } from "./HubSection";

import type { HelpHubDTO } from "@/types/help-hub/help-hub";
interface HubContactSectionProps {
  hub: HelpHubDTO;
}

interface ContactRow {
  key: string;
  label: string;
  value: string;
  href?: string;
  copyable?: boolean;
}

/**
 * "Reach us" — phone, email, hours, address and the owner's curated links.
 *
 * Every row is omitted when its data is absent rather than shown empty, so a
 * business that has only ever set up a Genie still gets a section that looks
 * finished rather than half-filled.
 */
export const HubContactSection = ({ hub }: HubContactSectionProps) => {
  const { branding, contact } = hub;
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const copyToClipboard = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((current) => (current === key ? null : current)), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const rows: ContactRow[] = [];
  if (hub.phoneNumber) {
    rows.push({
      key: "phone",
      label: "Phone",
      value: hub.phoneNumber,
      href: `tel:${hub.phoneNumber}`,
    });
  }
  for (const phone of hub.extraPhones) {
    rows.push({
      key: `phone-${phone.id}`,
      label: phone.label || "Phone",
      value: phone.number,
      href: `tel:${phone.number}`,
    });
  }
  if (hub.emailAddress) {
    rows.push({
      key: "email",
      label: "Email",
      value: hub.emailAddress,
      href: `mailto:${hub.emailAddress}`,
      copyable: true,
    });
  }
  if (contact?.hours) {
    rows.push({ key: "hours", label: "Hours", value: contact.hours });
  }
  if (contact?.address) {
    rows.push({
      key: "address",
      label: "Find us",
      value: contact.address,
      href: contact.mapUrl ?? undefined,
    });
  }
  rows.push({ key: "chat", label: "The Genie", value: "Answers 24/7" });

  if (rows.length === 0 && hub.links.length === 0) return null;

  return (
    <HubSection id="reach-us" title="Reach us" genieIcon="phone" branding={branding}>
      <div className="grid gap-3 lg:grid-cols-[1.4fr_1fr]">
        <div className="divide-y divide-genie-border rounded-2xl border border-genie-border bg-white px-4">
          {rows.map((row) => (
            <div key={row.key} className="flex items-center justify-between gap-3 py-3.5">
              <span className="text-[13px] text-genie-text-muted">{row.label}</span>
              {row.key === "email" ? (
                <span className="min-w-0 text-right text-[14px] font-semibold" style={{ color: branding.primaryColor }}>
                  <CopyableEmail email={row.value} href={row.href} />
                </span>
              ) : row.href ? (
                <a
                  href={row.href}
                  target={row.href.startsWith("http") ? "_blank" : undefined}
                  rel={row.href.startsWith("http") ? "noreferrer noopener" : undefined}
                  onClick={row.href.startsWith("tel:") ? () => trackCallClick("hub_contact_section") : undefined}
                  className="truncate text-right text-[14px] font-semibold"
                  style={{ color: branding.primaryColor }}
                >
                  {row.value}
                </a>
              ) : (
                <span className="truncate text-right text-[14px] font-semibold text-genie-text-primary">
                  {row.value}
                </span>
              )}
            </div>
          ))}
        </div>

        {hub.links.length > 0 && (
          <div className="rounded-2xl border border-genie-border bg-white p-4">
            <div className="text-[12px] font-semibold uppercase tracking-wide text-genie-text-muted">Useful links</div>
            <ul className="mt-2.5 space-y-2">
              {hub.links.map((link) => (
                <li key={link.id}>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-[13.5px] underline-offset-2 hover:underline"
                    style={{ color: branding.primaryColor }}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </HubSection>
  );
};

export default HubContactSection;
